import Input from '@/components/basicComponents/input';
import Select from '@/components/basicComponents/select';
import { useResponsive } from '@/hooks/use-responsive';
import { buildTestId } from '@/lib/utils/testIds';
import { COUNTRIES, UNITED_STATES_STATES } from '@trace-8/trace8-schema';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { TwoColumnWrapper } from '../styles';
import { IRenderTemplateRow } from '../types';
import { evaluateDependencyItem, shouldRenderEmptyComponent } from '../utils';

interface AddressComponentProps {
  row: IRenderTemplateRow;
  pageName?: string;
  ignoreRequired: boolean;
}

export const AddressComponent = ({ row, pageName = '', ignoreRequired }: AddressComponentProps) => {
  const { isMobile } = useResponsive();
  const { control, unregister, getValues, setValue, watch } = useFormContext();
  const { name, config, validation, dependsOn } = row;
  const countryValue = watch(`${pageName}.${name}.${config?.mapping?.['country'] ?? 'country'}`);
  const dependentValue = dependsOn ? watch(`${pageName}.${dependsOn.name}`) : null;
  const hasValidOrDependency: boolean = dependsOn?.or
    ? dependsOn.or.some((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;
  const hasValidAndDependency: boolean = dependsOn?.and
    ? dependsOn.and.every((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;

  useEffect(() => {
    if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) {
      unregister(`${pageName}.${name}`);
    }
  }, [dependentValue, hasValidOrDependency, hasValidAndDependency]);

  useEffect(() => {
    const countryFieldName = `${pageName}.${name}.${config?.mapping?.['country'] ?? 'country'}`;
    const countryValue = getValues(countryFieldName);

    if (!countryValue && (!dependsOn || !shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency))) {
      setValue(countryFieldName, COUNTRIES[0]);
    }
  }, [dependentValue, hasValidOrDependency, hasValidAndDependency]);

  if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) return <></>;

  return (
    <>
      <TwoColumnWrapper isMobile={isMobile}>
        <Controller
          name={`${pageName}.${name}.${config?.mapping?.['streetAddress'] ?? 'streetAddress'}`}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              label={config?.labels?.['streetAddress'] ?? 'Street Address'}
              value={value || ''}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              errorText={error?.message}
              isRequiredField={!!validation?.required && !ignoreRequired}
              size="md"
              data-testid={name ? buildTestId('signature-packet', pageName, name, 'streetAddress') : undefined}
            />
          )}
        />
      </TwoColumnWrapper>
      <TwoColumnWrapper isMobile={isMobile}>
        <TwoColumnWrapper isMobile={isMobile}>
          <Controller
            name={`${pageName}.${name}.${config?.mapping?.['city'] ?? 'city'}`}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                label={config?.labels?.['city'] ?? 'City'}
                value={value || ''}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                errorText={error?.message}
                isRequiredField={!!validation?.required && !ignoreRequired}
                size="md"
                data-testid={name ? buildTestId('signature-packet', pageName, name, 'city') : undefined}
              />
            )}
          />
          <Controller
            name={`${pageName}.${name}.${config?.mapping?.['state'] ?? 'state'}`}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                {countryValue === 'United States' ? (
                  <Select
                    label={config?.labels?.['state'] ?? 'State'}
                    value={value || ''}
                    setValue={onChange}
                    options={[{ value: '', label: 'Select' }, ...UNITED_STATES_STATES.map((state) => ({ value: state, label: state }))]}
                    errorText={error?.message}
                    isRequiredField={!!validation?.required && !ignoreRequired}
                    size="md"
                    withSearch
                    data-testid={name ? buildTestId('signature-packet', pageName, name, 'state') : undefined}
                    data-testid-dropdown={name ? buildTestId('signature-packet', pageName, name, 'state', 'dropdown') : undefined}
                    data-testid-option={name ? buildTestId('signature-packet', pageName, name, 'state', 'option') : undefined}
                  />
                ) : (
                  <Input
                    label={config?.labels?.['state'] ?? 'State'}
                    value={value || ''}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    errorText={error?.message}
                    isRequiredField={!!validation?.required && !ignoreRequired}
                    size="md"
                    data-testid={name ? buildTestId('signature-packet', pageName, name, 'state') : undefined}
                  />
                )}
              </>
            )}
          />
        </TwoColumnWrapper>
        <TwoColumnWrapper isMobile={isMobile}>
          <Controller
            name={`${pageName}.${name}.${config?.mapping?.['zipCode'] ?? 'zipCode'}`}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                label={config?.labels?.['zipCode'] ?? 'Zip'}
                value={value || ''}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                errorText={error?.message}
                isRequiredField={!!validation?.required && !ignoreRequired}
                size="md"
                data-testid={name ? buildTestId('signature-packet', pageName, name, 'zipCode') : undefined}
              />
            )}
          />
          <div>
            <Controller
              name={`${pageName}.${name}.${config?.mapping?.['country'] ?? 'country'}`}
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  label={config?.labels?.['country'] ?? 'Country'}
                  value={value || ''}
                  setValue={(value) => {
                    onChange(value);
                    if (COUNTRIES[0] !== value) {
                      setValue(`${pageName}.${name}.${config?.mapping?.['state'] ?? 'state'}`, '');
                    }
                  }}
                  options={[{ value: null, label: 'Select' }, ...COUNTRIES.map((country) => ({ value: country, label: country }))]}
                  errorText={error?.message}
                  isRequiredField={!!validation?.required && !ignoreRequired}
                  size="md"
                  withSearch
                  data-testid={name ? buildTestId('signature-packet', pageName, name, 'country') : undefined}
                  data-testid-dropdown={name ? buildTestId('signature-packet', pageName, name, 'country', 'dropdown') : undefined}
                  data-testid-option={name ? buildTestId('signature-packet', pageName, name, 'country', 'option') : undefined}
                />
              )}
            />
          </div>
        </TwoColumnWrapper>
      </TwoColumnWrapper>
    </>
  );
};
